/**
 * Emails to listing owners (docs/email-plan.md). Sending goes through the
 * sendListingEmail Cloud Function; the record of sent emails (/mail) is read
 * here, super admin only.
 */
import { useCallback, useEffect, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';
import { ListingEmailKind } from '../utils/emailDrafts';

export type MailKind = 'pendingAlert' | 'deletedAlert' | 'invite' | 'contact' | 'contactOwner' | ListingEmailKind;
export type MailStatus = 'sent' | 'failed' | 'notSent';

export interface MailRecord {
  id: string;
  kind: MailKind;
  to: string[];
  subject: string;
  text: string;
  status: MailStatus;
  createdAt: string;
}

/** Emails recorded for one listing, newest first. */
export function useListingEmails(listingId: string | undefined) {
  const [mails, setMails] = useState<MailRecord[]>([]);

  useEffect(() => {
    if (!listingId) {
      setMails([]);
      return;
    }
    const unsubscribe = onSnapshot(
      query(collection(db, 'mail'), where('listingId', '==', listingId)),
      (snapshot) =>
        setMails(
          snapshot.docs
            .map((d) => ({ id: d.id, ...d.data() } as MailRecord))
            .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
        ),
      () => setMails([])
    );
    return () => unsubscribe();
  }, [listingId]);

  return mails;
}

interface SendInput {
  listingId: string;
  kind: ListingEmailKind;
  subject: string;
  message: string;
}

export function useSendListingEmail() {
  const [sending, setSending] = useState(false);

  /** Returns the status and who it went to; throws when it could not be sent. */
  const send = useCallback(async (input: SendInput) => {
    setSending(true);
    try {
      const result = await httpsCallable<SendInput, { status: MailStatus; to: string[] }>(
        functions,
        'sendListingEmail'
      )(input);
      return result.data;
    } finally {
      setSending(false);
    }
  }, []);

  return { sending, send };
}
